import { Button, Dialog } from '@mui/material'
import React, { useEffect, useState } from 'react'
import CustomFormatDatePicker from '../../../components/common/CustomFormatDatePicker'
import { useTheme } from '../../../components/hooks/useTheme'
import { validateMin } from '../../../utils/validateMin'
import useConfirm from '../../../components/hooks/useConfirm'
import ReactQuill from 'react-quill'
import { post_PaymentRequest } from '../../../services/PaymentRequest'
import useAuth from '../../../components/hooks/useAuth'
import { branchTimezone } from '../../../utils/summaryCalculation'
import colorConfigs from '../../../configs/colorConfigs'
import EmailValidatedComponent from '../../../components/EmailValidatedComponent'
import Cookies from 'js-cookie'
import useTenant from '../../../components/hooks/useTenant'
import moment from 'moment'

const modules = {
    toolbar: [
        [{ font: [] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }, { align: [] }],
        ["link", "image", "video"],
        ["clean"],
    ],
};

type PaymentRequestProps = {
    open: boolean;
    onClose: () => void;
    isCompletedOrCancelled: boolean;
    setPleaseWait: (value: boolean) => void;
    reservationId: number;
    summaryData: any;
    preferredDateFormat: string;
    formattedCreatedAt: string;
    isPaymentRequestType: boolean;
    refreshThePaymentRequest: () => void;
}

const PaymentRequest = ({
    open,
    onClose,
    isCompletedOrCancelled,
    setPleaseWait,
    reservationId,
    summaryData,
    preferredDateFormat,
    formattedCreatedAt,
    isPaymentRequestType,
    refreshThePaymentRequest
}: PaymentRequestProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { theme, themeStyles } = useTheme();
    const { auth }: any = useAuth();
    const { handleTenantError }: any = useTenant();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const [totalAmount, setTotalAmount] = useState<number>(0);
    const [amount, setAmount] = useState<number>(0);
    const [email, setEmail] = useState<string>("");
    const [dueDate, setDueDate] = useState<string>("");
    const [comments, setComments] = useState<string>("");
    const UpdateTimezone = branchTimezone(formattedCreatedAt);
    const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

    useEffect(() => {
        if (open) {
            setDueDate(moment(new Date(UpdateTimezone)).format("YYYY-MM-DD"));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    useEffect(() => {
        if (open && summaryData) {
            if (isPaymentRequestType) {
                let paymentAmount = 0;
                paymentAmount = summaryData?.outstandingBalance;

                setAmount(paymentAmount);
                setTotalAmount(paymentAmount);
            } else {
                let remainingSecurityDepositAmount = 0;
                let securityDiff = summaryData?.securityDepositAmount - summaryData?.securityDepositAmountPaid;
                remainingSecurityDepositAmount = securityDiff > 0 ? securityDiff : 0;

                setTotalAmount(remainingSecurityDepositAmount);
                setAmount(remainingSecurityDepositAmount);
            }

            setEmail(summaryData?.customerEmail);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, summaryData])

    const resetForm = () => {
        onClose();
        setTotalAmount(0);
        setAmount(0);
        setEmail("");
        setDueDate("");
        setComments("");
    }

    const handleOfflinePayment = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isCompletedOrCancelled) {
            if (amount > totalAmount) {
                setConfirmData({
                    title: 'Warning',
                    summary: isPaymentRequestType
                        ? "Payment Request amount cannot exceed the outstanding balance."
                        : "Security Deposit Request amount cannot exceed the security deposit amount to pay.",
                    confirmBtn: 'Ok',
                    isConfirm: (callback: any) => {
                        callback();
                    }
                });

                setConfirmPopup(true);
                return;
            }

            setConfirmData({
                title: isPaymentRequestType ? 'Confirm for Payment Request' : 'Confirm for Security Deposit Request',
                summary: `Are you sure you want to sent ${isPaymentRequestType ? "Payment" : "Security Deposit"} Request?`,
                confirmBtn: 'Yes',
                isConfirm: async (callback: any) => {
                    callback();

                    setPleaseWait(true);

                    await post_PaymentRequest({
                        reservationId: reservationId,
                        requestType: isPaymentRequestType ? "Payment" : "Security Deposit",
                        amount: amount,
                        email: email,
                        status: "Sent",
                        paymentLink: "",
                        uniqid: "",
                        dueDate: dueDate,
                        paidAt: "",
                        createdBy: auth.givenname,
                        createdAt: new Date(UpdateTimezone).toLocaleString(),
                        comment: comments?.trim()
                    }, auth?.id, tenantId)
                        .then(() => {
                            refreshThePaymentRequest();
                            resetForm();
                            setPleaseWait(false);
                            handleTenantError(null);
                        })
                        .catch((error) => {
                            setPleaseWait(false);
                            handleTenantError(error);
                        })
                }
            });

            setConfirmPopup(true);
        }
    };

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullScreen={false}
            sx={{
                '& .MuiDialog-paper': {
                    maxWidth: '600px',
                    width: '100%',
                    borderRadius: '16px',
                },
            }}
        >
            <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320.591 320.591"
                    className={`w-3 cursor-pointer float-right ${closeIconFill}`}
                    onClick={() => onClose()}
                >
                    <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
                    <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
                </svg>
                <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                    <div className="w-full mt-2">
                        <div className={`w-full`}>
                            <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                                <div className="flex items-center gap-1.5">
                                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>{`Request ${isPaymentRequestType ? "Payment" : "Security Deposit"}`}</h2>
                                </div>
                            </div>

                            <div className="mt-2 p-2">
                                <div className="w-full">
                                    <form onSubmit={handleOfflinePayment}>
                                        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Amount</label>
                                                <input
                                                    type="number"
                                                    step={0.01}
                                                    min={0}
                                                    className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                    value={amount}
                                                    onChange={(e) =>
                                                        setAmount(validateMin(e.target.value, 0))
                                                    }
                                                    required
                                                />
                                            </div>

                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Email</label>
                                                <EmailValidatedComponent
                                                    text={email}
                                                    setText={setEmail}
                                                    required={true}
                                                />
                                            </div>

                                            <div>
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Due Date</label>
                                                <CustomFormatDatePicker
                                                    value={dueDate}
                                                    onChange={setDueDate}
                                                    dateFormat={preferredDateFormat}
                                                    required={true}
                                                />
                                            </div>

                                            <div />

                                            <div className="col-span-2 relative">
                                                <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Comments</label>
                                                <ReactQuill
                                                    style={{ height: "150px", width: "100%" }}
                                                    theme="snow"
                                                    value={comments}
                                                    modules={modules}
                                                    onChange={setComments}
                                                />
                                            </div>
                                        </div>

                                        <div className="flex gap-4 mt-[6rem]">
                                            <Button variant="contained"
                                                sx={{
                                                    backgroundColor: colorConfigs.btn.cancel.bg,
                                                    border: colorConfigs.btn.cancel.border,
                                                    color: colorConfigs.btn.cancel.color
                                                }}
                                                onClick={onClose}
                                            >
                                                Cancel
                                            </Button>

                                            <Button variant="contained"
                                                sx={{
                                                    backgroundColor: colorConfigs.btn.main.bg
                                                }}
                                                type="submit"
                                            >
                                                Send Request
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

export default PaymentRequest